import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  Tooltip,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../api/api';

const formatMoney = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return Number(value).toLocaleString('es-CR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
};

/**
 * Modal de gestión de precios de un ítem del catálogo.
 * Antes vivía embebido en Catalogo.js; se extrajo porque tiene su propio
 * ciclo de vida (CRUD de precios) y así se puede tocar sin arriesgar el
 * resto de la pantalla de catálogo.
 *
 * Props:
 * - open: boolean
 * - catalogo: ítem seleccionado (o null)
 * - userIdPet: id de la veterinaria del usuario actual
 * - listasPrecio: listas de precio disponibles (cargadas por el padre)
 * - onClose: () => void
 * - onNotify: (mensaje: string) => void  -> para el snackbar global
 * - onPreciosChange: (idCatalogo, preciosArray) => void -> sincroniza el
 *   resumen de precios que se muestra en la tabla principal
 */
const PreciosModal = ({
  open,
  catalogo,
  userIdPet,
  listasPrecio,
  onClose,
  onNotify,
  onPreciosChange,
}) => {
  const [precios, setPrecios] = useState([]);
  const [preciosLoading, setPreciosLoading] = useState(false);
  const [precioForm, setPrecioForm] = useState({ precio: '', id_lista_precio: '' });
  const [editingPrecioId, setEditingPrecioId] = useState(null);
  const [precioError, setPrecioError] = useState(null);

  // Cargar precios cada vez que se abre el modal para un ítem
  useEffect(() => {
    if (open && catalogo) {
      setEditingPrecioId(null);
      setPrecioForm({ precio: '', id_lista_precio: '' });
      setPrecioError(null);
      loadPreciosOfItem(catalogo.id_catalogo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, catalogo]);

  const loadPreciosOfItem = async (idCatalogo) => {
    setPreciosLoading(true);
    setPrecioError(null);
    try {
      const res = await api.get(`/precios/find/${idCatalogo}`);
      const data = Array.isArray(res.data) ? res.data : [];
      setPrecios(data);
      onPreciosChange?.(idCatalogo, data);
    } catch (err) {
      console.error('Error al cargar precios:', err);
      setPrecioError('No se pudieron cargar los precios: ' + (err.response?.data?.detail || err.message));
      setPrecios([]);
    } finally {
      setPreciosLoading(false);
    }
  };

  const handleClose = () => {
    setPrecios([]);
    setEditingPrecioId(null);
    setPrecioForm({ precio: '', id_lista_precio: '' });
    setPrecioError(null);
    onClose();
  };

  const handleStartEditPrecio = (precioItem) => {
    setEditingPrecioId(precioItem.id);
    setPrecioForm({
      precio: String(precioItem.precio),
      id_lista_precio: precioItem.id_lista_precio ? String(precioItem.id_lista_precio) : '',
    });
    setPrecioError(null);
  };

  const handleCancelEditPrecio = () => {
    setEditingPrecioId(null);
    setPrecioForm({ precio: '', id_lista_precio: '' });
    setPrecioError(null);
  };

  const handleSavePrecio = async () => {
    if (!catalogo) return;

    const precioNum = parseFloat(precioForm.precio);
    if (isNaN(precioNum) || precioNum < 0) {
      setPrecioError('El precio debe ser un número mayor o igual a cero.');
      return;
    }

    // Evitar duplicados exactos de precio + lista
    const listaId = precioForm.id_lista_precio ? Number(precioForm.id_lista_precio) : null;
    const alreadyExists = precios.some(
      p =>
        Number(p.precio) === precioNum &&
        (p.id_lista_precio || null) === listaId &&
        p.id !== editingPrecioId
    );
    if (alreadyExists) {
      setPrecioError('Ya existe ese precio para la misma lista de precios.');
      return;
    }

    try {
      setPreciosLoading(true);
      setPrecioError(null);

      const payload = {
        id_pet: userIdPet,
        id_catalogo: catalogo.id_catalogo,
        precio: precioNum,
        id_lista_precio: listaId,
      };

      if (editingPrecioId) {
        await api.put(`/precios/${editingPrecioId}`, payload);
        onNotify?.('Precio actualizado correctamente');
      } else {
        await api.post('/precios/', payload);
        onNotify?.('Precio agregado correctamente');
      }

      setEditingPrecioId(null);
      setPrecioForm({ precio: '', id_lista_precio: '' });
      await loadPreciosOfItem(catalogo.id_catalogo);
    } catch (err) {
      console.error('Error al guardar precio:', err);
      setPrecioError(
        'Error al guardar precio: ' +
          (err.response?.data?.detail || err.message || 'Error desconocido')
      );
    } finally {
      setPreciosLoading(false);
    }
  };

  const handleDeletePrecio = async (idPrecio) => {
    if (!window.confirm('¿Eliminar este precio? Esta acción no se puede deshacer.')) return;
    try {
      setPreciosLoading(true);
      await api.delete(`/precios/${idPrecio}`);
      onNotify?.('Precio eliminado correctamente');
      await loadPreciosOfItem(catalogo.id_catalogo);
    } catch (err) {
      console.error('Error al eliminar precio:', err);
      setPrecioError('Error al eliminar precio: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPreciosLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Precios del ítem</Typography>
          {catalogo && (
            <Typography variant="body2" color="text.secondary">
              {catalogo.referencia} — {catalogo.descripcion}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Un producto puede tener <strong>varios precios</strong>, cada uno asociado a una{' '}
          <strong>lista de precios</strong> (ej: Público, Mayorista, Clínica).
          Si no eliges lista, se guarda como precio general.
        </Alert>

        {precioError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPrecioError(null)}>
            {precioError}
          </Alert>
        )}

        {/* Formulario agregar / editar precio */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
          <Typography variant="subtitle2" gutterBottom>
            {editingPrecioId ? 'Editar precio' : 'Agregar nuevo precio'}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Precio de venta"
              type="number"
              value={precioForm.precio}
              onChange={(e) => setPrecioForm(prev => ({ ...prev, precio: e.target.value }))}
              size="small"
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              placeholder="Ej: 15000.00"
              disabled={preciosLoading}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="lista-precio-label">Lista de precios (opcional)</InputLabel>
              <Select
                labelId="lista-precio-label"
                label="Lista de precios (opcional)"
                value={precioForm.id_lista_precio}
                onChange={(e) => setPrecioForm(prev => ({ ...prev, id_lista_precio: e.target.value }))}
                disabled={preciosLoading}
              >
                <MenuItem value="">
                  <em>Sin lista (precio general)</em>
                </MenuItem>
                {listasPrecio.map((lista) => (
                  <MenuItem key={lista.id_lista_precio} value={String(lista.id_lista_precio)}>
                    {lista.nombre_lista}
                    {lista.es_lista_default ? ' ★ (default)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {listasPrecio.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No hay listas de precio creadas. Puedes guardar el precio sin lista o crear listas desde el módulo de Listas de Precio.
              </Typography>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSavePrecio}
                disabled={preciosLoading || !precioForm.precio}
                startIcon={editingPrecioId ? <EditIcon /> : <AddIcon />}
              >
                {editingPrecioId ? 'Actualizar' : 'Agregar'}
              </Button>
              {editingPrecioId && (
                <Button variant="outlined" onClick={handleCancelEditPrecio} disabled={preciosLoading}>
                  Cancelar
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Precios registrados ({precios.length})
        </Typography>

        {preciosLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : precios.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary">
              Este ítem aún no tiene precios registrados.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Usa el formulario de arriba para agregar el primero.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Precio</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Lista de precios</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {precios
                  .slice()
                  .sort((a, b) => Number(b.precio) - Number(a.precio))
                  .map((p, index) => (
                    <TableRow
                      key={p.id}
                      selected={editingPrecioId === p.id}
                      sx={{ bgcolor: editingPrecioId === p.id ? 'action.selected' : 'inherit' }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" flexDirection="column" alignItems="flex-end">
                          <Typography
                            variant="body2"
                            fontWeight={index === 0 ? 'bold' : 'regular'}
                            color={index === 0 ? 'success.main' : 'text.primary'}
                          >
                            $ {formatMoney(p.precio)}
                            {index === 0 && precios.length > 1 && (
                              <Chip
                                label="Más alto"
                                size="small"
                                color="success"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            (Sin IVA: ${((parseFloat(p.precio) || 0) / 1.13).toFixed(4)})
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {p.nombre_lista && p.nombre_lista !== 'Sin lista' ? (
                          <Chip
                            label={p.nombre_lista + (p.es_lista_default ? ' ★' : '')}
                            size="small"
                            color={p.es_lista_default ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">Sin lista</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleStartEditPrecio(p)}
                            disabled={preciosLoading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeletePrecio(p.id)}
                            disabled={preciosLoading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreciosModal;
